import React from "react";

import BuildIcon from "@mui/icons-material/Build";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudIcon from "@mui/icons-material/Cloud";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import {
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemIcon,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import type { EnvironmentInfo } from "./pipelineDataHooks";

// ── Constants ──────────────────────────────────────────────────────────────────

const ENV_TYPE_CONFIG: Record<
  string,
  { icon: React.ReactElement; color: string }
> = {
  dev: { icon: <BuildIcon fontSize="small" />, color: "info" },
  development: { icon: <BuildIcon fontSize="small" />, color: "info" },
  staging: { icon: <RocketLaunchIcon fontSize="small" />, color: "warning" },
  production: { icon: <CloudIcon fontSize="small" />, color: "success" },
};

// ── Types ──────────────────────────────────────────────────────────────────────

export interface WorkflowSidebarProps {
  environments: EnvironmentInfo[];
  selectedEnvSlug: string;
  onSelectEnv: (slug: string) => void;
  /**
   * Set of environment IDs that have a deployed stack for the current project.
   * Environments not in this set are rendered as "Not deployed" with a
   * muted appearance and a promote CTA tooltip.
   */
  deployedEnvIds?: Set<number>;
  /**
   * Called when the user clicks the "Promote" chip on an undeployed env.
   * If not provided, the chip is informational only.
   */
  onPromote?: (targetSlug: string) => void;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function WorkflowSidebar({
  environments,
  selectedEnvSlug,
  onSelectEnv,
  deployedEnvIds,
  onPromote,
}: WorkflowSidebarProps): React.ReactElement {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: 240,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
        }}
      >
        <Typography
          variant="caption"
          fontWeight={600}
          color="text.secondary"
          sx={{ textTransform: "uppercase", letterSpacing: 1 }}
        >
          Workflows
        </Typography>
      </Box>

      {/* Environment list */}
      <List disablePadding sx={{ flex: 1, overflow: "auto" }}>
        {environments.map((env) => {
          const config = ENV_TYPE_CONFIG[env.envType] ?? {
            icon: <CloudIcon fontSize="small" />,
            color: "default",
          };
          const isSelected = env.slug === selectedEnvSlug;
          const isDeployed = deployedEnvIds ? deployedEnvIds.has(env.id) : true;

          return (
            <ListItemButton
              key={env.id}
              selected={isSelected}
              onClick={() => onSelectEnv(env.slug)}
              sx={{
                py: 1.25,
                px: 2,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.04)}`,
                "&:last-child": { borderBottom: "none" },
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
                "&.Mui-selected": {
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                  pl: 1.625,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                },
                ...(isDeployed ? {} : { opacity: 0.55 }),
              }}
            >
              <ListItemIcon
                sx={{ minWidth: 28, color: `${config.color}.main` }}
              >
                {config.icon}
              </ListItemIcon>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {env.name}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {env.envType}
                </Typography>
              </Box>
              {!isDeployed && (
                <Tooltip
                  title={
                    onPromote
                      ? "Not deployed yet — click to promote"
                      : "Not deployed"
                  }
                >
                  <Chip
                    label="Not deployed"
                    size="small"
                    variant="outlined"
                    icon={<HourglassEmptyIcon sx={{ fontSize: 12 }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPromote?.(env.slug);
                    }}
                    sx={{
                      fontSize: "0.625rem",
                      height: 18,
                      borderColor: alpha(theme.palette.divider, 0.3),
                      "& .MuiChip-icon": { fontSize: 12, mr: 0.25 },
                      cursor: onPromote ? "pointer" : "default",
                    }}
                  />
                </Tooltip>
              )}
              {isDeployed && env.status === "active" && (
                <CheckCircleIcon sx={{ fontSize: 14, color: "success.main" }} />
              )}
              {isDeployed && env.status === "error" && (
                <ErrorIcon sx={{ fontSize: 14, color: "error.main" }} />
              )}
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
}
